import { ThumbsUp, ThumbsDown, Shield, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSkillReviews, useVouchSkill } from '@/hooks/useSkillReviews';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import type { Skill } from '@/lib/skills';

interface TrustScoreProps {
  skill: Skill;
}

export function TrustScore({ skill }: TrustScoreProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { data, isLoading } = useSkillReviews(skill.id);
  const { mutate: vouch, isPending } = useVouchSkill();

  const handleVote = (vote: '+' | '-') => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in with Nostr to vouch for skills.',
        variant: 'destructive',
      });
      return;
    }

    vouch({
      skillEventId: skill.id,
      skillPubkey: skill.pubkey,
      vote,
    }, {
      onSuccess: () => {
        toast({
          title: vote === '+' ? 'Vouched!' : 'Flagged',
          description: vote === '+' 
            ? 'Your endorsement has been recorded.' 
            : 'Your concern has been recorded.',
        });
      },
      onError: () => {
        toast({
          title: 'Failed to submit',
          description: 'Could not record your vote. Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const trustScore = data?.trustScore;
  const score = trustScore?.score ?? 0;
  const vouches = trustScore?.vouches ?? 0;
  const flags = trustScore?.flags ?? 0;
  const userVouch = trustScore?.userVouch;

  // Determine trust level
  const getTrustLevel = () => {
    if (vouches === 0 && flags === 0) {
      return { label: 'Unreviewed', color: 'text-muted-foreground', icon: Shield, bg: 'bg-white/[0.05]' };
    }
    if (score >= 3) {
      return { label: 'Trusted', color: 'text-emerald-400', icon: ShieldCheck, bg: 'bg-emerald-500/10' };
    }
    if (score >= 1) {
      return { label: 'Promising', color: 'text-amber-400', icon: Shield, bg: 'bg-amber-500/10' };
    }
    if (score <= -2) {
      return { label: 'Flagged', color: 'text-red-400', icon: ShieldAlert, bg: 'bg-red-500/10' };
    }
    return { label: 'Mixed', color: 'text-muted-foreground', icon: Shield, bg: 'bg-white/[0.05]' };
  };

  const trust = getTrustLevel();
  const TrustIcon = trust.icon;

  return (
    <Card className="glass-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrustIcon className={`w-5 h-5 ${trust.color}`} />
          Web of Trust
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trust Badge */}
        <div className={`p-3 rounded-xl ${trust.bg} border border-white/[0.06]`}>
          <div className="flex items-center justify-between">
            <span className={`font-medium ${trust.color}`}>{trust.label}</span>
            <span className="text-sm text-muted-foreground">
              {vouches + flags} review{vouches + flags !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Vote Stats */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-emerald-400">
            <ThumbsUp className="w-4 h-4" />
            <span>{vouches}</span>
          </div>
          <div className="flex items-center gap-1.5 text-red-400">
            <ThumbsDown className="w-4 h-4" />
            <span>{flags}</span>
          </div>
        </div>

        {/* Vote Buttons */}
        <div className="flex gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={userVouch === '+' ? 'default' : 'outline'}
                className={`flex-1 gap-2 ${userVouch === '+' ? 'bg-emerald-600 hover:bg-emerald-500 border-0' : 'border-white/[0.08] hover:bg-emerald-500/10 hover:border-emerald-500/30'}`}
                onClick={() => handleVote('+')}
                disabled={isPending || userVouch === '+'}
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsUp className="w-4 h-4" />
                )}
                Vouch
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Endorse this skill as safe and useful</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={userVouch === '-' ? 'default' : 'outline'}
                className={`flex-1 gap-2 ${userVouch === '-' ? 'bg-red-600 hover:bg-red-500 border-0' : 'border-white/[0.08] hover:bg-red-500/10 hover:border-red-500/30'}`}
                onClick={() => handleVote('-')}
                disabled={isPending || userVouch === '-'}
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsDown className="w-4 h-4" />
                )}
                Flag
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Report concerns about this skill</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {userVouch && (
          <p className="text-xs text-muted-foreground text-center">
            You {userVouch === '+' ? 'vouched for' : 'flagged'} this skill
          </p>
        )}

        <p className="text-xs text-muted-foreground leading-relaxed">
          Reviews from the Nostr community help agents decide which skills to trust. 
          Your vote is public and tied to your Nostr identity.
        </p>
      </CardContent>
    </Card>
  );
}

// Compact version for skill cards
export function TrustBadge({ skillId }: { skillId: string }) {
  const { data, isLoading } = useSkillReviews(skillId);

  if (isLoading || !data) {
    return null;
  }

  const { vouches, flags } = data.trustScore;
  
  if (vouches === 0 && flags === 0) {
    return null;
  }

  const score = vouches - flags;
  
  if (score >= 3) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-1 text-emerald-400">
            <ShieldCheck className="w-3.5 h-3.5" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Trusted by {vouches} users</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (score <= -2) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-1 text-red-400">
            <ShieldAlert className="w-3.5 h-3.5" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Flagged by {flags} users</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return null;
}
